import { z } from 'zod';
import { chatPresentationSchema } from './chatPresentation';

export const chatToolResultSchema = z.object({
  toolName: z.string(),
  toolCallId: z.string(),
  success: z.boolean(),
  isFinalFailure: z.boolean().optional(),
  at: z.string().datetime().optional(),
  publicSummary: z.string(),
  code: z.string().optional(),
  data: chatPresentationSchema.optional(),
});
export type ChatToolResult = z.infer<typeof chatToolResultSchema>;

/**
 * The public name of a result that holds documents for their layout. The routing tool that held them is internal,
 * so the chat lists the hold, and shows its Review cards, under this name.
 */
export const DOCUMENT_LAYOUT_REVIEW_TOOL = 'review_document_layout';

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

function outputValue(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      return outputValue(JSON.parse(value));
    } catch {
      return null;
    }
  }
  if (!record(value)) return null;
  if ((value.type === 'json' || value.type === 'text') && 'value' in value) return outputValue(value.value);
  return value;
}

export function projectChatToolResult(value: unknown): ChatToolResult | null {
  if (!record(value)) return null;
  const output = 'output' in value ? outputValue(value.output) : value;
  if (!output || typeof output.success !== 'boolean') return null;
  const presentation = chatPresentationSchema.safeParse(output.data);
  const holdsDocuments = presentation.success && presentation.data.kind === 'document-onboarding';
  const parsed = chatToolResultSchema.safeParse({
    toolName: holdsDocuments ? DOCUMENT_LAYOUT_REVIEW_TOOL : value.toolName,
    toolCallId: value.toolCallId,
    success: output.success,
    publicSummary: typeof output.publicSummary === 'string' ? output.publicSummary : '',
    ...(typeof output.code === 'string' ? { code: output.code } : {}),
    ...(presentation.success ? { data: presentation.data } : {}),
  });
  return parsed.success ? parsed.data : null;
}

export function projectChatToolResults(values: readonly unknown[]): ChatToolResult[] {
  const results: ChatToolResult[] = [];
  for (const value of values) {
    const result = projectChatToolResult(value);
    if (result) results.push(result);
  }
  return results;
}
